import { formatWallClock, localDayKey, type AttendanceReportResponse, type AttendanceReportStamp } from '@wf/shared'
import { STAMP_KIND_LABEL } from '../corrections/labels'
import { formatHMS } from '../time/format'
import { pauseSecondsByDay } from './timeMath'

/** Wochentags-Kürzel + 'TT.MM.' für die Tag-Spalte (aus dem Berlin-Tagesschlüssel, tz-neutral geparst). */
function dayLabel(day: string): string {
  const date = new Date(day + 'T12:00:00')
  const wd = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][date.getDay()]
  const [, m, d] = day.split('-')
  return `${wd} ${d}.${m}.`
}

/**
 * Monatsnachweis-Tabelle (v6-Parität): eine Zeile je Tag mit Beginn/Ende/Pause/Netto + Stempel-Chips.
 * Netto/Beginn/Ende kommen fertig aus dem Server-Report (B-5-Mitternachts-Split) — hier wird NICHT neu
 * gerechnet, und ALLE Tage/Uhrzeiten laufen über die tz-fixierten Werkstatt-Helfer (localDayKey/
 * formatWallClock), nie über die Geräte-Zeitzone. Entwertete Stempel bleiben sichtbar (durchgestrichen);
 * ihr Pflicht-Grund steht SICHTBAR unter der Zeile (Tablet-first — title-Tooltips reichen nicht).
 * `canEdit` (nur Chef/Zweitchef) blendet Entwerten (✕ am Chip) und „+ Nachtrag" je Tag ein.
 */
export function ReportTable({
  report,
  canEdit = false,
  onVoid,
  onAdd,
}: {
  report: AttendanceReportResponse
  canEdit?: boolean
  onVoid?: (stamp: AttendanceReportStamp) => void
  onAdd?: (day: string) => void
}) {
  const today = localDayKey(Date.now())
  const pauseByDay = pauseSecondsByDay(report.stamps)
  const stampsByDay = new Map<string, AttendanceReportStamp[]>()
  for (const s of report.stamps) {
    const day = localDayKey(s.tsMs)
    const arr = stampsByDay.get(day) ?? []
    arr.push(s)
    stampsByDay.set(day, arr)
  }

  if (report.days.length === 0) {
    return (
      <div className="wf-empty">
        <div className="wf-empty__title">Keine Stempel</div>
        <div className="wf-empty__text">In diesem Zeitraum wurde noch keine Zeit erfasst.</div>
      </div>
    )
  }

  return (
    <div className="wf-table" aria-label={`Zeitnachweis ${report.user.name}`}>
      <div className="wf-table__head">
        <span className="wf-ztcol wf-ztcol--day">Tag</span>
        <span className="wf-ztcol">Beginn</span>
        <span className="wf-ztcol">Ende</span>
        <span className="wf-ztcol">Pause</span>
        <span className="wf-ztcol wf-ztcol--net">Netto</span>
      </div>
      {report.days.map((d) => {
        const over = d.workedSeconds > 8 * 3600
        const pause = pauseByDay[d.day] ?? 0
        const chips = stampsByDay.get(d.day) ?? []
        const documented = chips.filter((s) => s.voided || s.correctedBy != null)
        const rowClass = ['wf-table__row', 'wf-ztrow', over ? 'wf-ztrow--over' : '', d.day === today ? 'wf-ztrow--today' : '']
          .filter(Boolean)
          .join(' ')
        return (
          <div key={d.day} className={rowClass}>
            <div className="wf-ztrow__line">
              <span className="wf-ztcol wf-ztcol--day">
                <strong>{dayLabel(d.day)}</strong>
                {d.day === today ? ' · heute' : ''}
              </span>
              <span className="wf-ztcol wf-mono">{d.startMs != null ? formatWallClock(d.startMs) : '–'}</span>
              <span className="wf-ztcol wf-mono">{d.endMs != null ? formatWallClock(d.endMs) : '–'}</span>
              <span className="wf-ztcol wf-mono">{pause ? formatHMS(pause) : '–'}</span>
              <span className={over ? 'wf-ztcol wf-ztcol--net wf-mono wf-ztnet--over' : 'wf-ztcol wf-ztcol--net wf-mono'}>
                {formatHMS(d.workedSeconds)}
              </span>
            </div>
            <div className="wf-ztrow__chips">
              {over ? (
                <span className="wf-prio wf-prio--hoch" title="Mehr als 8 Std. (§16 ArbZG)">
                  ⚠ &gt;8h
                </span>
              ) : null}
              {chips.map((s) => {
                // Korrigierte Chips markiert allein der Akzent-Rand (wf-tag--corr) — kein ✏️-Anhängsel mehr.
                const cls = ['wf-tag', s.voided ? 'wf-tag--void' : '', !s.voided && s.correctedBy ? 'wf-tag--corr' : '']
                  .filter(Boolean)
                  .join(' ')
                return (
                  <span key={s.id} className={cls}>
                    <span className={`wf-tagdot wf-tagdot--${s.kind}`} aria-hidden="true" />
                    {STAMP_KIND_LABEL[s.kind]} {formatWallClock(s.tsMs)}
                    {canEdit && !s.voided && onVoid ? (
                      <button
                        type="button"
                        className="wf-tagx"
                        title="Stempel entwerten (mit Grund)"
                        aria-label={`Stempel ${STAMP_KIND_LABEL[s.kind]} ${formatWallClock(s.tsMs)} entwerten`}
                        onClick={() => onVoid(s)}
                      >
                        ✕
                      </button>
                    ) : null}
                  </span>
                )
              })}
              {canEdit && onAdd ? (
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm wf-ztrow__add" onClick={() => onAdd(d.day)}>
                  + Nachtrag
                </button>
              ) : null}
            </div>
            {documented.length > 0 ? (
              <div className="wf-ztrow__reasons">
                {documented.map((s) => (
                  <span key={`r-${s.id}`}>
                    {s.voided ? 'Entwertet' : 'Nachtrag'} · {STAMP_KIND_LABEL[s.kind]} {formatWallClock(s.tsMs)}
                    {s.reason ? `: ${s.reason}` : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
